import React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Card } from '../../../../components/common/Card/Card';
import { Typography } from '../../../../components/common/Typography/Typography';
import { ModCategory, ModCategoryType } from '../../../../constants/enums';
import { getProfileRoute } from '../../../../constants/routes';
import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { getThumbnailSrc, getYoutubeEmbedUrl } from '../../utils/mediaUtils';
import styles from './ModCard.module.css';
import { ModCardProps } from './ModCard.types';

export const ModCard: React.FC<ModCardProps> = ({ mod, onMouseEnter }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = React.useState(false);
  const youtubeEmbedUrl = getYoutubeEmbedUrl(mod.youtubeUrl);
  const profileUrl = getProfileRoute(mod.id);

  const categoryBadgeLabel: Record<ModCategoryType, string> = {
    [ModCategory.VR]: t.nav.vrMods,
    [ModCategory.UEVR]: t.nav.uevrMods,
    [ModCategory.HEAD_TRACKING]: t.nav.headTrackingMods,
  };
  const badgeText = categoryBadgeLabel[mod.category] || String(mod.category).toUpperCase();

  const versionTrimmed = mod.version?.trim();
  const displayVersion =
    versionTrimmed && versionTrimmed.replace(/^v/i, '').trim()
      ? versionTrimmed.startsWith('v') || versionTrimmed.startsWith('V')
        ? versionTrimmed
        : `v${versionTrimmed}`
      : null;

  const validTags = Array.isArray(mod.tags)
    ? mod.tags.map((tag) => (typeof tag === 'string' ? tag.trim() : '')).filter(Boolean)
    : [];
  const cardTags = validTags.slice(0, 3);

  React.useEffect(() => {
    if (!isPlaying) return;

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data && data.event === 'onStateChange') {
          // YouTube PlayerState: 2 = PAUSED, 0 = ENDED
          if (data.info === 2 || data.info === 0) {
            setIsPlaying(false);
          }
        }
      } catch {
        // Ignore non-JSON postMessage events
      }
    };

    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [isPlaying]);

  const handlePlayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsPlaying(true);
  };

  const handleCloseVideoClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsPlaying(false);
  };

  const autoplayEmbedUrl = youtubeEmbedUrl ? `${youtubeEmbedUrl}?autoplay=1&enablejsapi=1` : null;

  const handleCardClick = () => {
    if (isPlaying) return;
    navigate(profileUrl);
  };

  return (
    <Card
      className={`mod-card ${isPlaying ? '' : styles.cardClickable}`}
      onClick={handleCardClick}
      onMouseEnter={onMouseEnter ? () => onMouseEnter(mod) : undefined}
    >
      <div className={styles.imageContainer}>
        {isPlaying && autoplayEmbedUrl ? (
          <div className={styles.videoWrapper}>
            <iframe
              src={autoplayEmbedUrl}
              title={`${mod.title} video`}
              className={styles.video}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              onLoad={(e) => {
                try {
                  e.currentTarget.contentWindow?.postMessage(
                    JSON.stringify({ event: 'listening', id: 1 }),
                    '*'
                  );
                } catch {
                  // Ignore postMessage origin restrictions
                }
              }}
            />
            <button
              type="button"
              className={styles.closeVideoButton}
              onClick={handleCloseVideoClick}
              aria-label={t.mods.closeVideoFor({ title: mod.title })}
            >
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                width="16"
                height="16"
                aria-hidden="true"
              >
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            <img src={getThumbnailSrc(mod.thumbnailUrl)} alt={mod.title} className={styles.image} />
            {youtubeEmbedUrl && (
              <button
                type="button"
                className={styles.playButton}
                onClick={handlePlayClick}
                aria-label={t.mods.playVideoFor({ title: mod.title })}
              >
                <svg
                  className={styles.playIcon}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="18"
                  height="18"
                  aria-hidden="true"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}
          </>
        )}
        <span className={styles.badge}>{badgeText}</span>
      </div>
      <div className={styles.content}>
        <Typography variant="h3" className={styles.title}>
          <Link to={profileUrl} className={styles.titleLink} onClick={(e) => e.stopPropagation()}>
            {mod.title}
          </Link>
        </Typography>
        <div className={styles.creatorMetaRow}>
          {mod.gameTitle && (
            <Typography variant="body2" className={styles.gameTitle}>
              {t.mods.forGame({ game: mod.gameTitle })}
            </Typography>
          )}
          {(mod.modAuthor || mod.author) && (
            <Typography variant="body2" className={styles.moddedBy}>
              {t.mods.moddedBy({ author: (mod.modAuthor || mod.author) as string })}
            </Typography>
          )}
        </div>
        {mod.status && (
          <Typography variant="body2" className={styles.status}>
            {mod.status}
          </Typography>
        )}
        <Typography variant="body1" className={styles.description}>
          {mod.description}
        </Typography>
        {cardTags.length > 0 && (
          <div className={styles.tagsContainer}>
            {cardTags.map((tag) => (
              <span key={tag} className={styles.tagChip}>
                {tag}
              </span>
            ))}
          </div>
        )}
        {displayVersion && (
          <div className={styles.metaRow}>
            <Typography variant="caption" className={styles.meta}>
              {displayVersion}
            </Typography>
          </div>
        )}
      </div>
    </Card>
  );
};
